<template>
  <div class="page-options">
    <base-section>
      <div class="container">
        <div class="options-body">
          <base-card class="base-card-options">
            <h2 class="title title-light title-caps text-center">{{$t('views.options.title')}}</h2>

            <form class="base-form" @submit.prevent="submitForm">
              <div class="base-form-fields">
                <div class="base-form-radios">
                  <label class="radio-field">
                    <input type="radio" name="trip_type" value="one-way" v-model="tripType">
                    <span class="radio-label text">{{$t('booking.one_way')}}</span>
                  </label>

                  <label class="radio-field">
                    <input type="radio" name="trip_type" value="round-trip" v-model="tripType">
                    <span class="radio-label text">{{$t('booking.round_trip')}}</span>
                  </label>
                </div>

                <div class="base-form-field">
                  <label for="origin" class="text text-tiny text-bold text-clear">{{$t('booking.from')}}</label>
                  <i class="fa-regular fa-chevron-down"></i>
                  <select id="origin" :value="origin" @change="onOriginChange">
                    <option
                      v-for="option in locations"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div class="base-form-field">
                  <label for="destination" class="text text-tiny text-bold text-clear">{{$t('booking.to')}}</label>
                  <i class="fa-regular fa-chevron-down"></i>
                  <select id="destination" :value="destination" @change="onDestinationChange">
                    <option
                      v-for="option in locations"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div class="base-form-calendars">
                  <div class="base-form-calendar base-form-calendar-1-2">
                    <label class="base-form-label text text-tiny text-bold text-clear">{{$t('booking.departure_date')}}</label>
                    <i class="fa-regular fa-calendar"></i>
                    <input
                      ref="departurePicker"
                      type="text"
                      class="base-input"
                      :placeholder="$t('forms.select_date')"
                      readonly
                    />
                  </div>

                  <div class="base-form-calendar base-form-calendar-1-2" v-if="tripType === 'round-trip'">
                    <label class="base-form-label text text-tiny text-bold text-clear">{{$t('booking.return_date')}}</label>
                    <i class="fa-regular fa-calendar"></i>
                    <input
                      ref="returnPicker"
                      type="text"
                      class="base-input"
                      :placeholder="$t('forms.select_date')"
                      readonly
                    />
                  </div>
                </div>

                <div class="base-form-quantity">
                  <label class="text text-tiny text-bold text-clear">{{$t('booking.passengers')}}</label>

                  <div class="quantity-options">
                    <div class="quantity-button quantity-button-minus" @click="removePassenger"><i class="fa-sharp fa-regular fa-minus"></i></div>
                    <span class="quantity-number text text-big text-clear">{{ passengersCount }}</span>
                    <div class="quantity-button quantity-button-plus" @click="addPassenger"><i class="fa-sharp fa-regular fa-plus"></i></div>
                  </div>
                </div>
              </div>

              <div class="base-form-footer" v-if="formValidated">
                <base-button class="base-button-pri" type="submit">{{$t('buttons.search')}}</base-button>
              </div>
            </form>
          </base-card>


          <div class="options-note">
            <p class="text text-center">{{$t('views.options.note')}}</p>
          </div>
        </div>
      </div>
    </base-section>
    <teleport to=".simply-flights-frontend">
      <base-loading v-if="loading"></base-loading>
    </teleport>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'
import { French } from 'flatpickr/dist/l10n/fr.js'

export default {
  data() {
    return {
      origin: '',
      destination: '',
      passengersCount: 1,
      tripType: 'one-way',
      departureDate: null,
      returnDate: null,
      departureFp: null,
      returnFp: null,
    }
  },
  computed: {
    ...mapState( 'optionsModule', ['locations', 'daysOfWeek', 'loading', 'maxPassengers'] ),
    dateFormat() { return 'Y-m-d' },
    flatpickrLocale() {
      const locale =
        this.$i18n?.locale ||
        document.documentElement.lang ||
        'en'

      return locale.startsWith('fr')
        ? French
        : undefined
    },
    formValidated() {
      if( this.tripType === 'round-trip' ) {
        if( this.departureDate && this.returnDate ) {
          return true
        }
      } else {
        if( this.departureDate ) {
          return true
        }
      }
    }
  },
  methods: {
    onOriginChange(event) {
      const selected = event.target.value
      this.origin = selected
      this.destination = this.getOtherLocation(selected)
    },
    onDestinationChange(event) {
      const selected = event.target.value
      this.destination = selected
      this.origin = this.getOtherLocation(selected)
    },
    getOtherLocation(selectedValue) {
      const other = this.locations.find( l => l.value !== selectedValue )
      return other ? other.value : ''
    },
    addPassenger() {
      if( this.passengersCount < this.maxPassengers ) this.passengersCount++
    },
    removePassenger() {
      if( this.passengersCount > 1 ) this.passengersCount--
    },
    initDeparturePicker() {
      if( !this.$refs.departurePicker || !this.daysOfWeek?.length ) return
      if( this.departureFp) this.departureFp.destroy()

      this.departureFp = flatpickr(this.$refs.departurePicker, {
        ...(this.flatpickrLocale ? { locale: this.flatpickrLocale } : {}),
        minDate: 'today',
        dateFormat: this.dateFormat,
        disable: [
          date => !this.daysOfWeek.includes(date.getDay())
        ],
        onChange: (selectedDates, dateStr) => {
          this.departureDate = dateStr
          this.initReturnPicker()
        }
      })
    },
    initReturnPicker() {
      if (!this.$refs.returnPicker || !this.departureDate || !this.daysOfWeek?.length) return
      if (this.returnFp) this.returnFp.destroy()

      this.returnFp = flatpickr(this.$refs.returnPicker, {
        locale: this.flatpickrLocale,
        minDate: this.departureDate,
        dateFormat: this.dateFormat,

        disable: [
          date => !this.daysOfWeek.includes(date.getDay())
        ],
        onChange: (selectedDates, dateStr) => {
          this.returnDate = dateStr
        }
      })
    },
    submitForm() {
      const options = {
        origin: this.origin,
        destination: this.destination,
        tripType: this.tripType,
        passengersCount: this.passengersCount,
        departureDate: this.departureDate,
        returnDate: this.returnDate
      }

      this.$store.commit('optionsModule/SET_OPTIONS', options)
      this.$router.push('/reservation-flights#base-timeline')
    }
  },
  watch: {
    daysOfWeek(newDays) {
      if (newDays?.length) {
        this.initDeparturePicker()
      }
    },
    tripType(type) {
      if( type === 'one-way' ) {
        this.returnDate = null
        if( this.returnFp ) {
          this.returnFp.destroy()
          this.returnFp = null
        }
      } else {
        this.$nextTick(() => {
          this.initReturnPicker()
        })
      }
    }
  },
  mounted() {
    if (this.locations?.length >= 2 && !this.origin && !this.destination) {
      this.origin = this.locations[0].value
      this.destination = this.locations[1].value
    }

    this.$store.dispatch('optionsModule/readSchedules')

    this.$nextTick(() => {
      this.initDeparturePicker()
    })
  }
}
</script>